import React, { createContext, useContext, useEffect, useState } from 'react';
import { Session, User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppStore } from '../store';

interface AuthContextType {
  session: Session | null;
  user: User | null;
  userRole: string | null;
  isLoading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string, role: string) => Promise<{ error: Error | null, data: any | null }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: Error | null }>;
  updatePassword: (password: string) => Promise<{ error: Error | null }>;
  isAdmin: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [authTimeout, setAuthTimeout] = useState<NodeJS.Timeout | null>(null);
  const [authRetries, setAuthRetries] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const setCurrentUser = useAppStore(state => state.setCurrentUser);

  // Refs estables para usar dentro de listeners de larga vida (auth) sin
  // re-suscribir en cada cambio de pathname.
  const navigateRef = React.useRef(navigate);
  navigateRef.current = navigate;
  const locationRef = React.useRef(location);
  locationRef.current = location;
  const sessionRef = React.useRef<Session | null>(null);
  sessionRef.current = session;

  const getDefaultPathForRole = (role: string | null) => {
    if (role === 'Contenido') return '/contenido/calendario';
    if (role === 'Vendedor') return '/express';
    if (role === 'Producción') return '/productionmanager';
    if (role === 'Depósito') return '/express/stock';
    return '/';
  };

  const getPostLoginPath = (role: string | null) => {
    const stored = localStorage.getItem('lastPath');
    const defaultPath = getDefaultPathForRole(role);

    if (!stored) return defaultPath;
    if (stored === '/' && role === 'Contenido') return defaultPath;
    if (stored === '/login' || stored === '/reset-password') return defaultPath;

    return stored;
  };

  // Function to clear auth timeout
  const clearAuthTimeout = () => {
    if (authTimeout) {
      clearTimeout(authTimeout);
      setAuthTimeout(null);
    }
  };

  // Function to set auth timeout - increased to 30 seconds
  const setAuthTimeoutHandler = () => {
    clearAuthTimeout();
    const timeout = setTimeout(() => {
      if (isLoading) {
        console.warn('Auth timeout: Session verification is taking longer than expected');
        
        // If we've retried less than 3 times, try again
        if (authRetries < 3) {
          console.log(`Retrying session verification (attempt ${authRetries + 1}/3)...`);
          setAuthRetries(prev => prev + 1);
          initializeAuth(); // Retry the auth initialization
        } else {
          console.error('Auth timeout: Session verification failed after multiple attempts');
          setIsLoading(false);
          setSession(null);
          setUser(null);
          setUserRole(null);
          // Don't navigate here - let the protected route handle it
        }
      }
    }, 30000); // Increased to 30 seconds
    setAuthTimeout(timeout);
  };

  // Initialize auth function - extracted to be reusable for retries
  const initializeAuth = async () => {
    try {
      if (!isLoading) setIsLoading(true);
      setAuthTimeoutHandler();
      
      // Get session from Supabase with retry logic for network errors
      let session = null;
      let error = null;
      
      for (let attempt = 1; attempt <= 3; attempt++) {
        try {
          const { data: { session: sessionData }, error: sessionError } = await supabase.auth.getSession();
          session = sessionData;
          error = sessionError;
          break; // Success, exit retry loop
        } catch (retryError: any) {
          console.warn(`Auth session attempt ${attempt}/3 failed:`, retryError);
          
          // Only retry on network errors, not auth errors
          if (retryError.message?.includes('Failed to fetch') || 
              retryError.message?.includes('NetworkError') ||
              retryError.message?.includes('timeout') ||
              retryError.message?.includes('conexión')) {
            if (attempt < 3) {
              await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
              continue;
            }
          }
          
          error = retryError;
          break;
        }
      }
      
      if (error) {
        console.error('Error getting session after retries:', error);
        
        // Only sign out on actual auth errors, not network errors
        if (error.message?.includes('Invalid Refresh Token') || 
            error.message?.includes('Refresh Token Not Found') ||
            error.message?.includes('Invalid JWT')) {
          console.warn('Invalid refresh token detected, signing out user');
          await signOut();
          return;
        }
        
        // For network errors, keep the user logged in but show a warning
        if (error.message?.includes('Failed to fetch') || 
            error.message?.includes('NetworkError') ||
            error.message?.includes('timeout') ||
            error.message?.includes('conexión')) {
          console.warn('Network error during auth check, keeping user logged in');
          // Don't clear the session on network errors - keep user logged in
          setIsLoading(false);
          clearAuthTimeout();
          return;
        }
        
        // For other errors, clear state
        setSession(null);
        setUser(null);
        setUserRole(null);
        setCurrentUser(null);
        return;
      }
      
      if (session) {
        setSession(session);
        setUser(session.user);
        
        // Get user role from app_role field in auth.users
        // Check multiple possible locations for the role
        let role = session.user.app_role || 
                   session.user.app_metadata?.app_role || 
                   session.user.user_metadata?.app_role;
        
        // Default to Vendedor if still no role
        role = role || 'Vendedor';
        
        console.log('User role detected:', role);
        
        setUserRole(role);
        setCurrentUser({
          id: session.user.id,
          email: session.user.email,
          role: role,
        });

        // Only redirect if we're on the login page
        if (location.pathname === '/login') {
          navigate(getPostLoginPath(role));
        }
      } else {
        // No session found
        setSession(null);
        setUser(null);
        setUserRole(null);
        setCurrentUser(null);
        
        // If we're not on an auth page, save the current path
        if (location.pathname !== '/login' && location.pathname !== '/reset-password') {
          localStorage.setItem('lastPath', location.pathname);
        }
      }
    } catch (error: any) {
      console.error('Error initializing auth:', error);
      
      // Only sign out on actual auth errors, not network errors
      if (error.message?.includes('Invalid Refresh Token') || 
          error.message?.includes('Refresh Token Not Found') ||
          error.message?.includes('Invalid JWT')) {
        console.warn('Invalid refresh token detected in error, signing out user');
        await signOut();
        return;
      }
      
      // For network errors, don't clear the session
      if (error.message?.includes('Failed to fetch') || 
          error.message?.includes('NetworkError') ||
          error.message?.includes('timeout') ||
          error.message?.includes('conexión')) {
        console.warn('Network error during auth initialization, keeping user logged in');
        setIsLoading(false);
        clearAuthTimeout();
        return;
      }
      
      // Clear state on other errors
      setSession(null);
      setUser(null);
      setUserRole(null);
      setCurrentUser(null);
    } finally {
      setIsLoading(false);
      clearAuthTimeout();
      setAuthRetries(0); // Reset retry counter on success or final failure
    }
  };

  // Save current path to localStorage when it changes
  // But only if we're not on an auth page and the user is logged in
  useEffect(() => {
    if (user && location.pathname !== '/login' && location.pathname !== '/reset-password') {
      localStorage.setItem('lastPath', location.pathname);
    }
  }, [location.pathname, user]);

  // Initialize auth on component mount. Importante: el array de deps debe
  // estar vacío para que la suscripción a onAuthStateChange viva durante toda
  // la sesión del cliente. Si la re-suscribimos en cada cambio de ruta, además
  // de ser ineficiente, dispara setStates innecesarios en cada navegación
  // (lo que puede causar re-renders que pisan estado de modales y forms).
  useEffect(() => {
    initializeAuth();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, newSession) => {
        // Evitamos logs ruidosos en producción salvo eventos relevantes.
        if (event !== 'TOKEN_REFRESHED' && event !== 'INITIAL_SESSION') {
          console.log('Auth state change event:', event);
        }

        if (event === 'TOKEN_REFRESHED' && !newSession) {
          console.warn('Token refresh failed, signing out user');
          setSession(null);
          setUser(null);
          setUserRole(null);
          setCurrentUser(null);
          localStorage.removeItem('lastPath');
          navigateRef.current('/login');
          return;
        }

        const prevUserId = sessionRef.current?.user?.id ?? null;
        const newUserId = newSession?.user?.id ?? null;

        // TOKEN_REFRESHED y INITIAL_SESSION para el mismo usuario no deben
        // tocar estado: evita re-renders globales que blanquean formularios.
        if (
          (event === 'TOKEN_REFRESHED' || event === 'INITIAL_SESSION') &&
          newUserId === prevUserId &&
          newUserId !== null
        ) {
          // Igual sincronizamos la session por si cambió el access_token,
          // pero usando una verificación rápida de igualdad por ref.
          if (newSession && newSession !== sessionRef.current) {
            setSession(newSession);
          }
          return;
        }

        setSession(newSession);
        setUser(newSession?.user ?? null);

        if (newSession?.user) {
          let role = newSession.user.app_role ||
                     newSession.user.app_metadata?.app_role ||
                     newSession.user.user_metadata?.app_role;
          role = role || 'Vendedor';

          setUserRole(role);
          setCurrentUser({
            id: newSession.user.id,
            email: newSession.user.email,
            role: role,
          });

          // Solo navegamos en un sign-in real (cambio de usuario), no en
          // restauraciones de sesión por foco de pestaña.
          if (event === 'SIGNED_IN' && newUserId !== prevUserId) {
            navigateRef.current(getPostLoginPath(role));
          }
        } else {
          setUserRole(null);
          setCurrentUser(null);

          if (event === 'SIGNED_OUT') {
            localStorage.removeItem('lastPath');
          }
        }

        setIsLoading(false);
        clearAuthTimeout();
      }
    );

    return () => {
      subscription.unsubscribe();
      clearAuthTimeout();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signIn = async (email: string, password: string) => {
    try {
      setIsLoading(true);
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      
      if (!error && data.session) {
        // Session is available immediately after sign in
        setSession(data.session);
        setUser(data.user);
        
        // Get user role
        const role = data.user?.app_role || 
                    data.user?.app_metadata?.app_role || 
                    data.user?.user_metadata?.app_role;
        
        setUserRole(role);
        setCurrentUser({
          id: data.user.id,
          email: data.user.email,
          role: role,
        });
        
        // Navigate to last path or home page
        navigate(getPostLoginPath(role || null));
      }
      
      return { error };
    } catch (error) {
      return { error: error as Error };
    } finally {
      setIsLoading(false);
    }
  };

  const signUp = async (email: string, password: string, role: string) => {
    try {
      setIsLoading(true);
      
      // Create user with role using hybrid RPC function (compatible with existing system)
      const { data: authData, error: authError } = await supabase.rpc('create_user_hybrid', {
        user_email: email,
        user_password: password,
        user_role: role
      });

      if (authError) {
        throw new Error(authError.message);
      }

      return { error: null, data: authData };
    } catch (error) {
      console.error('Error signing up:', error);
      return { error: error as Error, data: null };
    } finally {
      setIsLoading(false);
    }
  };

  const signOut = async () => {
    try {
      setIsLoading(true);
      // Attempt to sign out from Supabase, but don't let failures block the local cleanup
      await supabase.auth.signOut().catch(error => {
        console.warn('Supabase signOut failed:', error);
        // Continue with local cleanup even if server-side logout fails
      });
    } catch (error) {
      console.error('Error during sign out:', error);
    } finally {
      // Always clear local state and redirect, regardless of server-side logout success
      setSession(null);
      setUser(null);
      setUserRole(null);
      setCurrentUser(null);
      localStorage.removeItem('lastPath');
      setIsLoading(false);
      navigate('/login');
    }
  };

  const resetPassword = async (email: string) => {
    try {
      setIsLoading(true);
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      return { error };
    } catch (error) {
      return { error: error as Error };
    } finally {
      setIsLoading(false);
    }
  };

  const updatePassword = async (password: string) => {
    try {
      setIsLoading(true);
      const { error } = await supabase.auth.updateUser({
        password,
      });
      return { error };
    } catch (error) {
      return { error: error as Error };
    } finally {
      setIsLoading(false);
    }
  };

  // Check if user is admin - this is a critical check that determines menu access
  const isAdmin = userRole === 'Administrador';

  const value = {
    session,
    user,
    userRole,
    isLoading,
    signIn,
    signUp,
    signOut,
    resetPassword,
    updatePassword,
    isAdmin,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};